import type { ISortableModel } from "./types";

// NOTE: Provider priority for model picker groups (higher = shown first).
// Keys must match the label `getProviderLabel` returns: the provider field
// is upper-cased on the first letter only (`"claude"` → `"Claude"`, not
// `"Anthropic"`), so the Anthropic entry is keyed `"Claude"`. Mirrors the
// server-side `apps/nsai/server/src/bot-shared/bot-model-priority.ts`.
export const PROVIDER_PRIORITY: Record<string, number> = {
  Openai: 100,
  Claude: 90,
  Gemini: 80,
  Grok: 70,
  Mistral: 60,
  Deepseek: 50,
  GLM: 45,
  Kimi: 44,
  Qwen: 40,
  Ollama: 30,
  "On-Prem AI Model": 10,
};

// NOTE: Model priority within provider (higher = shown first)
const MODEL_PRIORITY: Record<string, number> = {
  // NOTE: OpenAI - GPT-6, then the GPT-5 series (newest first)
  // NOTE: NSAI-7075 — newest generation first: GPT-6.1 Sol leads the GPT-6
  // family, then Astra, Sol and Luna, all above GPT-5.6. Fractional so no row
  // other providers' models are compared to has to be renumbered.
  "gpt-6.1-sol": 116.5,
  "gpt-6-astra": 116,
  "gpt-6-sol": 115,
  "gpt-6-luna": 114.5,
  "gpt-5.6-sol": 114,
  "gpt-5.6-terra": 112,
  "gpt-5.6-luna": 110,
  "gpt-5.5-pro": 109,
  "gpt-5.5": 108,
  "gpt-5.4-pro": 105,
  "gpt-5.4": 104,
  "gpt-5.4-mini": 103,
  "gpt-5.4-nano": 102,
  "gpt-5.2": 100,
  "gpt-5.1": 99,
  "gpt-5": 98,
  "gpt-5-mini": 97,
  "gpt-5-nano": 96,
  "gpt-5-pro": 95,
  // NOTE: OpenAI - Reasoning models
  "o4-mini": 90,
  "o3-mini-high": 89,
  "o3-mini": 88,
  o1: 87,
  "o1-mini": 86,
  "o1-preview": 85,
  // NOTE: OpenAI - Image generation. Ranked under the text and reasoning
  // models (o1-preview is the lowest of those at 85), mirroring where the
  // Gemini image rows sit in the Google block, so an image model never
  // outranks a chat model in the picker. gpt-image-2 and gpt-image-1 stay
  // unranked (implicit 0) and therefore sort below these two, which is the
  // "newest first" order this map exists to express.
  "gpt-image-2.5-flare": 84,
  "gpt-image-2.5-sunburst": 83,
  // NOTE: OpenAI - GPT-4 series
  "gpt-4.1": 80,
  "gpt-4.1-nano": 79,
  "gpt-4o": 78,
  "gpt-4o-mini": 77,
  // NOTE: Anthropic — most-capable first (Mythos 5.1 > Fable 5.1 > Opus 5.5 > Opus 5 > Mythos 5 > Fable 5 > Opus 4.8 > 4.7 > 4.6 > Sonnet 5.5 > Sonnet 5 > older)
  "claude-mythos-5-1": 113,
  "claude-fable-5-1": 112,
  "claude-opus-5-5": 111,
  "claude-opus-5": 110,
  "claude-mythos-5": 109,
  "claude-fable-5": 108,
  "claude-opus-4-8": 107,
  "claude-opus-4-7": 106,
  "claude-opus-4-6": 105,
  // NOTE: NSAI-6732 — fractional on purpose: without a provider label this map
  // orders models across providers, so renumbering the Claude rows below to
  // make room would reorder them against other providers' models.
  "claude-sonnet-5-5": 104.5,
  "claude-sonnet-5": 104,
  // NOTE: NSAI-7075 — Haiku 5.5, the current generation's small model: under
  // Sonnet 5, above the 4.x Sonnets. Fractional for the reason given above.
  "claude-haiku-5-5": 103.5,
  "claude-sonnet-4-6": 103,
  "claude-sonnet-4-5-20250929": 102,
  "claude-opus-4-1-20250805": 101,
  "claude-opus-4-20250514": 100,
  "claude-sonnet-4-20250514": 99,
  // NOTE: Google
  "gemini-3.8-flash": 108,
  "gemini-3.7-flash": 107,
  "gemini-3.6-flash": 106,
  "gemini-3.5-flash": 105,
  "gemini-3.5-flash-lite": 104,
  "gemini-3.1-pro-preview": 103,
  "gemini-3-pro-preview": 102,
  "gemini-3-flash-preview": 101,
  "gemini-2.5-pro": 100,
  "gemini-2.5-flash": 99,
  "gemini-2.0-flash": 98,
  "gemini-1.5-pro": 97,
  "gemini-3.1-flash-image-preview": 96,
  "gemini-3-pro-image-preview": 95,
  "gemini-2.5-flash-image": 94,
  // NOTE: NSAI-6621 — Google video generation, under the image rows.
  "veo-3.1-generate-preview": 93,
  "veo-3.1-fast-generate-preview": 92,
  // NOTE: Grok
  "grok-4.7": 103,
  "grok-4.6": 102,
  "grok-4.5": 101,
  "grok-4-fast-reasoning": 100,
  "grok-4-fast-non-reasoning": 99,
  "grok-4-0709": 98,
  "grok-3": 97,
  "grok-3-mini": 96,
  "grok-code-fast-1": 95,
  "grok-2-vision-1212": 94,
  // NOTE: NSAI-0000 — the same seven models under the gateway ids the platform
  // rows now carry (`xai/*`, see data/models/grok.ts). Listed alongside the bare
  // vendor ids rather than replacing them: this map is keyed by `models.name`,
  // and a CUSTOM grok model an admin points at their own x.ai endpoint still
  // carries the bare id. Dropping those keys would silently sink every such row
  // to priority 0, at the bottom of its group.
  "xai/grok-4.7": 103,
  "xai/grok-4.6": 102,
  "xai/grok-4.5": 101,
  "xai/grok-4-fast-reasoning": 100,
  "xai/grok-4-fast-non-reasoning": 99,
  "xai/grok-4-0709": 98,
  "xai/grok-3": 97,
  "xai/grok-3-mini": 96,
  "xai/grok-code-fast-1": 95,
  // NOTE: Mistral
  // NOTE: NSAI-7075 — Large 4 and Medium 3.5 above the Large 3 generation.
  "mistral-large-4": 105,
  "mistral-medium-2604": 104,
  "mistral-large-2512": 103,
  "mistral-medium-2508": 102,
  "mistral-medium-2505": 101,
  "mistral-large-2411": 100,
  // NOTE: NSAI-0000 — gateway ids, listed alongside the bare vendor ids for the
  // same reason as the Grok block above. `mistral-large-2411` is retired and has
  // no platform row, so it has no gateway id to add.
  "mistral/mistral-large-4": 105,
  "mistral/mistral-medium-2604": 104,
  "mistral/mistral-large-2512": 103,
  "mistral/mistral-medium-2508": 102,
  "mistral/mistral-medium-2505": 101,
  // NOTE: DeepSeek — newest most-capable first, the same ordering rule the GLM
  // rows follow (NSAI-5991).
  "deepseek-v4-pro": 103,
  "deepseek-flash": 102,
  "deepseek-v4-flash": 101,
  "deepseek-chat": 100,
  "deepseek-coder": 99,
  // NOTE: NSAI-0000 — gateway ids, as above.
  "deepseek/deepseek-v4-pro": 103,
  "deepseek/deepseek-flash": 102,
  "deepseek/deepseek-v4-flash": 101,
  "deepseek/deepseek-chat": 100,
  "deepseek/deepseek-coder": 99,
  // NOTE: GLM (Zhipu AI) — newest most-capable first, per the ordering rule the
  // product owner set on the Fable 5.1 PR: 5.3 > 5.3 Flash > 5.2.
  "glm-5.3": 102,
  "glm-5.3-flash": 101,
  "glm-5.2": 100,
  // NOTE: Kimi (Moonshot AI)
  "kimi-k2.5": 100,
  // NOTE: NSAI-7075 — Qwen (hosted). The self-hosted Qwen rows are unranked.
  "openrouter/qwen/qwen3.8-max": 100,
};

// NOTE: Curated set of "popular" models surfaced in a dedicated group at the
// top of every model picker (web + mobile), so users reach for a fast, current
// model first. The popular models are ALSO kept in their provider groups — the
// Popular section is a shortcut, it does not remove them from anywhere. The
// array order is the order shown in the section. This is the single source of
// truth: edit this list to change the section contents everywhere. Matched
// against the API model `name`; only names that actually exist in a workspace
// render, so listing an absent model is harmless.
export const POPULAR_MODEL_NAMES: readonly string[] = [
  "gpt-5.5",
  "claude-opus-5-5",
  "claude-sonnet-5-5",
] as const;

const POPULAR_MODEL_ORDER: Record<string, number> = POPULAR_MODEL_NAMES.reduce<
  Record<string, number>
>((acc, name, index) => {
  acc[name] = index;
  return acc;
}, {});

// NOTE: A model is "popular" purely by its API `name` matching the curated
// list above.
export const isPopularModelName = (name: string): boolean =>
  Object.prototype.hasOwnProperty.call(POPULAR_MODEL_ORDER, name);

// NOTE: Returns the popular models present in `models`, ordered to match
// `POPULAR_MODEL_NAMES`. Used to build the top "Popular" picker group; the
// same models still appear in their provider groups (the section is additive).
export const filterPopularModels = <T extends ISortableModel>(
  models: T[],
): T[] =>
  models
    .filter((model) => isPopularModelName(model.name))
    .sort(
      (modelA, modelB) =>
        POPULAR_MODEL_ORDER[modelA.name] - POPULAR_MODEL_ORDER[modelB.name],
    );

/**
 * NOTE: Sort models so providers stay contiguous (PROVIDER_PRIORITY as primary key)
 * and newer/better models surface first within each provider (MODEL_PRIORITY as secondary).
 * `providerLabel` is optional so callers that pass raw API rows (no parser) still get
 * MODEL_PRIORITY-only ordering instead of a type error.
 */
export const sortModelsByPriority = <T extends ISortableModel>(
  models: T[],
): T[] => {
  return [...models].sort((modelA, modelB) => {
    const providerA = modelA.providerLabel
      ? (PROVIDER_PRIORITY[modelA.providerLabel] ?? 0)
      : 0;
    const providerB = modelB.providerLabel
      ? (PROVIDER_PRIORITY[modelB.providerLabel] ?? 0)
      : 0;
    if (providerA !== providerB) return providerB - providerA;
    const priorityA = MODEL_PRIORITY[modelA.name] ?? 0;
    const priorityB = MODEL_PRIORITY[modelB.name] ?? 0;
    return priorityB - priorityA;
  });
};
